// The map's state, and the one place it is written to the URL.
//
// Everything a reader can set is in the hash, so a shared link reproduces the
// same view: camera, query, filters, the selected church, the layers. The
// hash (not the query string) so the page is one cacheable document and the
// server never sees a reader's location.

export const DEFAULTS = Object.freeze({
  view: null,        // [zoom, lat, lng] or null = the tenant's home view
  q: '',             // search text
  lang: '',          // ISO 639-3 code
  day: -1,           // 0..6, -1 = any day
  after: -1,         // minutes since midnight, -1 = any hour
  now: false,        // "Mass in the next two hours"
  hc: false,         // confession
  ha: false,         // adoration
  hp: false,         // perpetual adoration
  lm: false,         // Latin Mass
  sc: false,         // has a school
  kind: '',          // 'c' | 'b' | 's' | 'm' | 'p' | ''
  near: null,        // [lat, lng, miles] or null
  place: '',         // 'city:Pasadena' | 'nb:silver-lake' | 'region:San Gabriel'
  sel: '',           // selected church id
  labels: true,
  heat: false,
});

const BOOL_KEYS = ['now', 'hc', 'ha', 'hp', 'lm', 'sc', 'heat'];

export function createStore(initial) {
  let state = { ...DEFAULTS, ...initial };
  const listeners = new Set();
  return {
    get: () => state,
    set(patch) {
      const next = { ...state, ...patch };
      let changed = false;
      for (const k of Object.keys(next)) if (!same(next[k], state[k])) { changed = true; break; }
      if (!changed) return state;
      const prev = state;
      state = next;
      for (const fn of listeners) fn(state, prev);
      return state;
    },
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  };
}

function same(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => v === b[i]);
  return false;
}

/** Every filter that narrows the set, as [key, value] pairs, for chips and relaxations. */
export function activeFilters(s) {
  const out = [];
  if (s.q) out.push(['q', s.q]);
  if (s.place) out.push(['place', s.place]);
  if (s.near) out.push(['near', s.near]);
  if (s.lang) out.push(['lang', s.lang]);
  if (s.day >= 0) out.push(['day', s.day]);
  if (s.after >= 0) out.push(['after', s.after]);
  if (s.now) out.push(['now', true]);
  for (const k of ['hc', 'ha', 'hp', 'lm', 'sc']) if (s[k]) out.push([k, true]);
  if (s.kind) out.push(['kind', s.kind]);
  return out;
}

export function clearedFilters() {
  return { q: '', place: '', near: null, lang: '', day: -1, after: -1, now: false, hc: false, ha: false, hp: false, lm: false, sc: false, kind: '' };
}

/* ── URL hash ─────────────────────────────────────────────────────────── */

export function toHash(s) {
  const p = new URLSearchParams();
  if (s.view) p.set('v', `${round(s.view[0], 2)}/${round(s.view[1], 5)}/${round(s.view[2], 5)}`);
  if (s.q) p.set('q', s.q);
  if (s.place) p.set('place', s.place);
  if (s.near) p.set('near', `${round(s.near[0], 5)},${round(s.near[1], 5)},${s.near[2]}`);
  if (s.lang) p.set('lang', s.lang);
  if (s.day >= 0) p.set('day', String(s.day));
  if (s.after >= 0) p.set('after', String(s.after));
  for (const k of BOOL_KEYS) if (s[k]) p.set(k, '1');
  if (s.kind) p.set('kind', s.kind);
  if (s.sel) p.set('sel', s.sel);
  if (!s.labels) p.set('labels', '0');
  const str = p.toString();
  return str ? '#' + str.replace(/%2F/g, '/').replace(/%2C/g, ',') : '';
}

export function fromHash(hash) {
  const out = {};
  const raw = (hash || '').replace(/^#/, '');
  if (!raw) return out;
  const p = new URLSearchParams(raw);
  const v = (p.get('v') || '').split('/').map(Number);
  if (v.length === 3 && v.every(Number.isFinite) && v[0] >= 0 && v[0] <= 22 && Math.abs(v[1]) <= 90 && Math.abs(v[2]) <= 180) out.view = v;
  if (p.get('q')) out.q = p.get('q').slice(0, 120);
  if (p.get('place')) out.place = p.get('place').slice(0, 120);
  const near = (p.get('near') || '').split(',').map(Number);
  if (near.length === 3 && near.every(Number.isFinite) && near[2] > 0) out.near = [near[0], near[1], Math.min(near[2], 500)];
  if (p.get('lang') && /^[a-z]{3}$/.test(p.get('lang'))) out.lang = p.get('lang');
  const day = Number(p.get('day')); if (p.has('day') && Number.isInteger(day) && day >= 0 && day <= 6) out.day = day;
  const after = Number(p.get('after')); if (p.has('after') && Number.isInteger(after) && after >= 0 && after < 1440) out.after = after;
  for (const k of BOOL_KEYS) if (p.get(k) === '1') out[k] = true;
  if (p.get('kind') && /^[cbsmp]$/.test(p.get('kind'))) out.kind = p.get('kind');
  if (p.get('sel')) out.sel = p.get('sel').slice(0, 80);
  if (p.get('labels') === '0') out.labels = false;
  return out;
}

function round(n, d) { return Number(Number(n).toFixed(d)); }

/** Writes the hash without adding history entries; the reader's back button stays theirs. */
export function bindUrl(store) {
  let writing = false;
  store.subscribe((s) => {
    const h = toHash(s);
    if (location.hash === h || (h === '' && location.hash === '')) return;
    writing = true;
    history.replaceState(null, '', h || location.pathname + location.search);
    writing = false;
  });
  window.addEventListener('hashchange', () => {
    if (writing) return;
    store.set({ ...DEFAULTS, ...fromHash(location.hash) });
  });
}
